import { useTranslation } from "react-i18next";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useCallback, useState } from "react";
import { CalculationOverviewSubject } from "./types";
import Sidebar from "./Components/Sidebar/Sidebar";
import ServiceList from "./Components/ServiceList";
import { StandalonePriceDetails, Tender } from "@/graphql/__generated__/graphql";

interface CalculationOverviewPageProps {
  initialOrganizationId?: string;
  isDialog?: boolean;
  tenderData?: Tender;
  initialServiceId?: number | null;
}

/**
 * CalculationOverviewPage - This component renders the calculation overview page
 * @returns A page component with a header and a calculation overview component
 */
function CalculationOverviewPage({ initialOrganizationId, isDialog, tenderData, initialServiceId }: CalculationOverviewPageProps) {
  const { t } = useTranslation();
  const [selectedSubject, setSelectedSubject] = useState<CalculationOverviewSubject | null>(null);
  const [selectedComparingSubject, setSelectedComparingSubject] = useState<CalculationOverviewSubject | null>(null);

  // State for standalone cars and purchases data, populated by ServiceList callback
  const [standaloneCars, setStandaloneCars] = useState<StandalonePriceDetails | undefined>(undefined);
  const [standalonePurchases, setStandalonePurchases] = useState<StandalonePriceDetails | undefined>(undefined);

  // Callback to receive standalone data from ServiceList to avoid duplicate FetchPriceDetails queries
  const handleStandaloneDataLoaded = useCallback((data: { standaloneCars?: StandalonePriceDetails; standalonePurchases?: StandalonePriceDetails }) => {
    setStandaloneCars(data.standaloneCars);
    setStandalonePurchases(data.standalonePurchases);
  }, []);

  return (
    <div className={`${isDialog ? "h-[90dvh]" : "h-[calc(100dvh-105px)]"} pl-2`}>
      {/* Main Container */}
      <ResizablePanelGroup orientation="horizontal" className="h-full flex grow min-h-0">
        {/* Services List Panel */}
        <ResizablePanel id="services-list-panel" defaultSize={50} minSize={30}>
          <div className="pr-4 pt-2">
            <h2 className="text-lg font-semibold">{t("edittender.calculationoverview.title")}</h2>
            <ServiceList
              setSelectedSubject={setSelectedSubject}
              selectedSubject={selectedSubject}
              initialOrganizationId={initialOrganizationId}
              defaultOrganizationId={isDialog ? undefined : initialOrganizationId}
              initialServiceId={initialServiceId ? Number(initialServiceId) : undefined}
              setSelectedComparingSubject={setSelectedComparingSubject}
              selectedComparingSubject={selectedComparingSubject}
              tenderData={tenderData}
              onStandaloneDataLoaded={handleStandaloneDataLoaded}
            />
          </div>
        </ResizablePanel>
        {/* Separator */}
        <ResizableHandle withHandle className="mr-4" />
        {/* Selected Service Panel */}
        <ResizablePanel id="service-details-panel" defaultSize={50} minSize={30} className="flex grow min-h-0">
          <ResizablePanelGroup orientation="horizontal" className="h-full">
            {/* Selected Service Panel */}
            <ResizablePanel id="selected-service-panel" defaultSize={selectedComparingSubject ? 50 : 100} minSize={6}>
              <div className="pt-4 h-full flex grow min-h-0">
                <Sidebar
                  subject={selectedSubject}
                  compareType="selected"
                  isComparing={selectedComparingSubject !== null}
                  tenderData={tenderData}
                  standaloneCars={standaloneCars ?? undefined}
                  standalonePurchases={standalonePurchases ?? undefined}
                />
              </div>
            </ResizablePanel>
            {/* Comparing Service Panel */}
            {selectedComparingSubject && (
              <>
                <ResizableHandle withHandle className="mr-4" />
                <ResizablePanel id="comparing-service-panel" defaultSize={50} minSize={6} className="flex grow min-h-0">
                  <div className="pt-4 flex grow min-h-0">
                    <Sidebar
                      subject={selectedComparingSubject}
                      compareType="comparing"
                      isComparing={selectedComparingSubject !== null}
                      tenderData={tenderData}
                      standaloneCars={standaloneCars ?? undefined}
                      standalonePurchases={standalonePurchases ?? undefined}
                    />
                  </div>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export default CalculationOverviewPage;
